import {
  Component,
  computed,
  ElementRef,
  input,
  model,
  OnDestroy,
  OnInit,
  signal,
} from '@angular/core';
import { NgClass } from '@angular/common';

export interface MultiselectOption {
  id: string;
  label: string;
}

@Component({
  selector: 'app-multiselect-dropdown',
  imports: [NgClass],
  templateUrl: './multiselect-dropdown.html',
})
export class MultiselectDropdownComponent implements OnInit, OnDestroy {
  options = input<MultiselectOption[]>([]);
  placeholder = input('Select…');
  selected = model<string[]>([]);
  error = input<boolean>(false);

  // Search box (~52px) + options list (max-h-52 = 208px).
  private static readonly DROPDOWN_HEIGHT = 264;
  private static readonly VIEWPORT_MARGIN = 8;

  protected open = signal(false);
  protected search = signal('');
  protected dropdownTop = signal(0);
  protected dropdownBottom = signal(0);
  protected dropdownLeft = signal(0);
  protected dropdownWidth = signal(0);
  protected openUpward = signal(false);
  protected dropdownMaxHeight = signal(MultiselectDropdownComponent.DROPDOWN_HEIGHT);

  protected filtered = computed(() => {
    const q = this.search().toLowerCase();
    return q ? this.options().filter((o) => o.label.toLowerCase().includes(q)) : this.options();
  });

  protected visibleChips = computed(() =>
    this.selected()
      .slice(0, 2)
      .map((id) => ({ id, label: this.options().find((o) => o.id === id)?.label ?? id })),
  );

  protected overflowCount = computed(() => Math.max(0, this.selected().length - 2));

  constructor(private el: ElementRef) {}

  ngOnInit(): void {
    window.addEventListener('scroll', this.onScroll, true);
    window.addEventListener('resize', this.onScroll);
    // Capture phase: modals stop click propagation, so a bubbling document listener never fires.
    document.addEventListener('pointerdown', this.onOutsidePointerDown, true);
  }

  ngOnDestroy(): void {
    window.removeEventListener('scroll', this.onScroll, true);
    window.removeEventListener('resize', this.onScroll);
    document.removeEventListener('pointerdown', this.onOutsidePointerDown, true);
  }

  private computePosition(): void {
    const rect = (this.el.nativeElement as HTMLElement).getBoundingClientRect();
    const h = MultiselectDropdownComponent.DROPDOWN_HEIGHT;
    const margin = MultiselectDropdownComponent.VIEWPORT_MARGIN;
    const spaceBelow = window.innerHeight - rect.bottom - 4 - margin;
    const spaceAbove = rect.top - 4 - margin;
    const up = spaceBelow < h && spaceAbove > spaceBelow;
    this.openUpward.set(up);
    this.dropdownLeft.set(rect.left);
    this.dropdownWidth.set(rect.width);
    // Never taller than the space on its side, so it can't be cut by the viewport;
    // the options list scrolls inside.
    this.dropdownMaxHeight.set(Math.min(h, up ? spaceAbove : spaceBelow));
    if (up) {
      this.dropdownBottom.set(window.innerHeight - rect.top + 4);
    } else {
      this.dropdownTop.set(rect.bottom + 4);
    }
  }

  /** The trigger can grow or shrink as chips change; keep the panel attached to it. */
  private repositionAfterRender(): void {
    if (this.open()) setTimeout(() => this.computePosition());
  }

  private onScroll = (): void => {
    if (!this.open()) return;
    this.computePosition();
  };

  private onOutsidePointerDown = (e: PointerEvent): void => {
    if (this.open() && !this.el.nativeElement.contains(e.target as Node)) this.open.set(false);
  };

  protected toggle(): void {
    if (!this.open()) {
      this.computePosition();
      this.search.set('');
    }
    this.open.update((v) => !v);
  }

  protected isSelected(id: string): boolean {
    return this.selected().includes(id);
  }

  protected toggleOption(id: string): void {
    this.selected.update((sel) => (sel.includes(id) ? sel.filter((s) => s !== id) : [...sel, id]));
    this.repositionAfterRender();
  }

  protected clearAll(): void {
    this.selected.set([]);
    this.repositionAfterRender();
  }

  protected deselect(id: string): void {
    this.selected.update((sel) => sel.filter((s) => s !== id));
    this.repositionAfterRender();
  }
}
